import { useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import InfoIcon from "@mui/icons-material/Info";
import ListIcon from "@mui/icons-material/List";
import MmsIcon from "@mui/icons-material/Mms";
import PeopleIcon from "@mui/icons-material/People";
import {
  FormControlLabel,
  IconButton,
  ListItemText,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import { useTheme } from "@mui/material/styles";
import Tab from "@mui/material/Tab";
import { format, isValid, parseISO } from "date-fns";
import { v4 as uuid } from "uuid";

import OmniDatePicker from "../ui/omniDatePicker";
import Dialog from "../ui/omniDialog";
import DialogActions from "../ui/omniDialogActions";
import DialogContent from "../ui/omniDialogContent";
import DialogTab from "../ui/omniDialogTab";
import DialogTabs from "../ui/omniDialogTabs";
import DialogTitle from "../ui/omniDialogTitle";
import OmniMenu from "../ui/omniMenu";
import OmniMenuItem from "../ui/omniMenuItem";
import OmniResponsiveButton from "../ui/omniResponsiveButton";
import OmniTextField from "../ui/omniTextField";
import OmniUrlField, { isAcceptableUrl } from "../ui/omniUrlField";
import AlertDialog from "./alertDialog";
import BotpressProductMarkersDialog from "./botpressProductMarkersDialog";

const FINANCIAL_MARKERS = [
  "HOMEOWNER",
  "RENTER",
  "HAS_MORTGAGE",
  "HIGH_HOME_EQUITY",
  "VEHICLE_OWNER",
  "HAS_AUTO_LOAN",
  "HIGH_AUTO_LOAN_RATE",
  "HAS_CHECKING",
  "HAS_SAVINGS",
  "HAS_DIRECT_DEPOSIT",
  "HAS_CREDIT_CARD",
  "HAS_CREDIT_CARD_DEBT",
  "HIGH_CREDIT_CARD_DEBT",
  "HIGH_CREDIT_UTILIZATION",
  "HAS_STUDENT_LOANS",
  "POOR_CREDIT",
  "FAIR_CREDIT",
  "GOOD_CREDIT",
  "EXCELLENT_CREDIT",
  "HIGH_DEBT_TO_INCOME",
  "BUSINESS_OWNER",
  "HAS_RETIREMENT_ACCOUNT",
  "HIGH_INVESTABLE_ASSETS",
  "COLLEGE_BOUND_CHILDREN",
  "NEW_MEMBER",
  "SINGLE_PRODUCT_MEMBER",
  "MULTI_PRODUCT_MEMBER",
  "FIRST_TIME_HOMEBUYER",
  "NEW_PARENT",
  "APPROACHING_RETIREMENT",
  "MORTGAGE_REFINANCE_CANDIDATE",
  "HELOC_CANDIDATE",
  "AUTO_REFINANCE_CANDIDATE",
  "DEBT_CONSOLIDATION_CANDIDATE",
  "BALANCE_TRANSFER_CANDIDATE",
  "CREDIT_BUILDER_CANDIDATE",
  "RETIREMENT_SAVINGS_OPPORTUNITY",
  "COLLEGE_SAVINGS_OPPORTUNITY",
  "WEALTH_MANAGEMENT_CANDIDATE",
  "BUSINESS_LENDING_CANDIDATE",
];

// Selectable product categories for the "Product" field. Edit to match the
// product catalog as needed. `value` is what is stored in the record's `type`
// column; `label` is what is shown in the dropdown.
const PRODUCT_TYPES = [
  { value: "loan", label: "Loan" },
  { value: "credit-card", label: "Credit Card" },
  { value: "checking", label: "Checking" },
  { value: "savings", label: "Savings" },
  { value: "deposit", label: "Deposit" },
  { value: "certificate", label: "Certificate" },
  { value: "retirement", label: "Retirement" },
  { value: "investment", label: "Investment" },
  { value: "education", label: "Education" },
  { value: "insurance", label: "Insurance" },
  { value: "business", label: "Business" },
];

const PRIORITY_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

// The product record stores dates as "yyyy-MM-dd" strings, while OmniDatePicker
// works with Date objects, so we convert in both directions.
const parseDateValue = value => {
  if (value == null || value === "") {
    return null;
  }

  // Stored values are calendar dates, but some carry a time/zone component
  // (e.g. an ISO timestamp written by a seed/migration). Parse only the leading
  // yyyy-MM-dd as a local date so the displayed day does not shift across time
  // zones (e.g. a value stored at UTC midnight showing as the previous day).
  if (typeof value === "string") {
    const dateOnly = value.trim().slice(0, 10);
    const parsedDate = /^\d{4}-\d{2}-\d{2}$/.test(dateOnly) ? parseISO(dateOnly) : parseISO(value);

    return isValid(parsedDate) ? parsedDate : null;
  }

  return isValid(value) ? value : null;
};

const formatDateValue = date => {
  if (date == null || !isValid(date)) {
    return "";
  }

  return format(date, "yyyy-MM-dd");
};

const parseStringField = value => {
  if (value == null || value.trim() === "") {
    return null;
  }

  return value.trim();
};

const parseRequiredStringField = value => {
  return parseStringField(value) || "";
};

const parseNumberField = value => {
  if (value == null || value.toString().trim() === "") {
    return null;
  }

  const parsedValue = Number(value);

  if (Number.isNaN(parsedValue)) {
    return null;
  }

  return parsedValue;
};

const cleanMarkerWeightInput = value => {
  if (value == null) {
    return "";
  }

  return value.toString().replace(/^0+(?=\d)/, "");
};

const isValidMarkerWeight = value => {
  if (value == null || value.toString().trim() === "") {
    return true;
  }

  const parsedValue = Number(value);

  if (Number.isNaN(parsedValue)) {
    return false;
  }

  return parsedValue >= 0 && parsedValue <= 100;
};

const parseMarkerWeight = value => {
  if (value == null || value.toString().trim() === "") {
    return 0;
  }

  return Number(value);
};

const normalizeMarkers = markers => {
  if (!Array.isArray(markers)) {
    return [];
  }

  return markers
    .map(marker => {
      if (typeof marker === "string") {
        return {
          marker: marker,
          value: true,
          weight: "0",
        };
      }

      if (marker?.marker != null) {
        return {
          marker: marker.marker,
          value: marker.value === true,
          weight: marker.weight == null ? "" : cleanMarkerWeightInput(marker.weight),
        };
      }

      return null;
    })
    .filter(Boolean);
};

const cleanProductRecord = productRecord => {
  const {
    computed,
    stale,
    description,
    price,
    state,
    matchSignals,
    softRequirements,
    ...cleanProductRecord
  } = productRecord || {};

  return cleanProductRecord;
};

const BotpressProductsDialog = props => {
  const {
    open,
    productRecord,
    onClose,
    onAccept,
    onDelete,
    dialogTitle = "Edit Product",
    okMessage = "Ok",
    cancelMessage = "Cancel",
    maxWidth = "sm",
    fullWidth = true,
    deleteMessage = "Delete",
  } = props;

  const theme = useTheme();

  const [type, setType] = useState(productRecord.type || "");
  const [title, setTitle] = useState(productRecord.title || "");
  const [summary, setSummary] = useState(productRecord.summary || "");
  const [startDate, setStartDate] = useState(productRecord.startDate || "");
  const [endDate, setEndDate] = useState(productRecord.endDate || "");
  const [linkUrl, setLinkUrl] = useState(productRecord.url || "");
  const [linkLabel, setLinkLabel] = useState(productRecord.assets?.cta || "");
  const [linkText, setLinkText] = useState(productRecord.assets?.disclaimer || "");
  const [priority, setPriority] = useState(
    productRecord.priority == null ? "" : productRecord.priority,
  );
  const [archived, setArchived] = useState(productRecord.archived === true);
  const [markers, setMarkers] = useState(normalizeMarkers(productRecord.markers));
  const [showConfirm, setShowConfirm] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const [selectedMarkerRecord, setSelectedMarkerRecord] = useState(null);
  const [markerMenuAnchor, setMarkerMenuAnchor] = useState(null);
  const [markerMenuRecord, setMarkerMenuRecord] = useState(null);

  const titleHasError = parseStringField(title) == null;
  const linkUrlHasError = !isAcceptableUrl(linkUrl, true);
  const markersHaveError = normalizeMarkers(markers).some(
    markerRecord => !isValidMarkerWeight(markerRecord.weight),
  );

  // Date bounds: an empty end date means the campaign is Ongoing. When both
  // dates are set, the end date must not fall before the start date. We compare
  // by calendar day (not raw instant) because a stored start date may carry a
  // time component while a picker-set end date lands on midnight — comparing
  // instants would flag the same day as a conflict.
  const startDateValue = parseDateValue(startDate);
  const endDateValue = parseDateValue(endDate);
  const toDayStart = dateValue =>
    dateValue
      ? new Date(dateValue.getFullYear(), dateValue.getMonth(), dateValue.getDate())
      : null;
  const startDay = toDayStart(startDateValue);
  const endDay = toDayStart(endDateValue);
  const dateRangeHasError = startDay != null && endDay != null && endDay < startDay;

  const formHasError = titleHasError || linkUrlHasError || markersHaveError || dateRangeHasError;

  // Existing records may carry a Product/Priority value that predates the current
  // option lists. Keep the stored value selectable so it still displays.
  const productOptions =
    type !== "" && !PRODUCT_TYPES.some(option => option.value === type)
      ? [{ value: type, label: type }, ...PRODUCT_TYPES]
      : PRODUCT_TYPES;
  const priorityOptions =
    priority !== "" && !PRIORITY_OPTIONS.some(option => String(option) === String(priority))
      ? [priority, ...PRIORITY_OPTIONS]
      : PRIORITY_OPTIONS;

  // Every normalized marker is shown in the Audience Attributes table
  // (normalizeMarkers always coerces value to a boolean, so none are dropped).
  const assignedMarkers = normalizeMarkers(markers);
  const assignedMarkerKeys = assignedMarkers.map(markerRecord => markerRecord.marker);

  const markerHeaderCellSx = {
    backgroundColor: theme.palette.grey[200],
    fontWeight: 600,
  };

  const openMarkerMenu = (event, markerRecord) => {
    setMarkerMenuAnchor(event.currentTarget);
    setMarkerMenuRecord(markerRecord);
  };

  const closeMarkerMenu = () => {
    setMarkerMenuAnchor(null);
    setMarkerMenuRecord(null);
  };

  const onAddMarker = () => {
    setSelectedMarkerRecord({ marker: "", value: true, weight: "" });
  };

  const onEditMarker = markerRecord => {
    setSelectedMarkerRecord(markerRecord);
  };

  const onDeleteMarker = markerName => {
    setMarkers(previousMarkers =>
      normalizeMarkers(previousMarkers).filter(markerRecord => markerRecord.marker !== markerName),
    );
  };

  const onAcceptMarker = markerRecord => {
    // The attribute being edited (empty when adding a new marker). Used so a
    // changed attribute updates the original row instead of adding a new one.
    const originalKey = selectedMarkerRecord?.marker || "";

    const cleanedMarker = {
      marker: markerRecord.marker,
      value: markerRecord.value === true,
      weight: cleanMarkerWeightInput(markerRecord.weight),
    };

    setMarkers(previousMarkers => {
      const normalizedMarkers = normalizeMarkers(previousMarkers);
      const editingExisting =
        originalKey !== "" && normalizedMarkers.some(existing => existing.marker === originalKey);

      if (editingExisting) {
        // Replace the edited row in place, dropping any other row that would
        // collide with the (possibly changed) attribute key.
        return normalizedMarkers
          .filter(
            existing => existing.marker === originalKey || existing.marker !== cleanedMarker.marker,
          )
          .map(existing => (existing.marker === originalKey ? cleanedMarker : existing));
      }

      // Adding: update in place if the attribute already exists, else append.
      if (normalizedMarkers.some(existing => existing.marker === cleanedMarker.marker)) {
        return normalizedMarkers.map(existing =>
          existing.marker === cleanedMarker.marker ? cleanedMarker : existing,
        );
      }

      return [...normalizedMarkers, cleanedMarker];
    });

    setSelectedMarkerRecord(null);
  };

  const onDeleteHandler = () => {
    onDelete(productRecord);
  };

  const onAcceptHandler = () => {
    if (formHasError) {
      return;
    }

    try {
      const editedProductRecord = cleanProductRecord({
        ...productRecord,

        type: parseRequiredStringField(type),
        title: parseRequiredStringField(title),
        archived: archived === true,

        summary: parseStringField(summary),
        startDate: parseStringField(startDate),
        endDate: parseStringField(endDate),
        url: parseStringField(linkUrl),
        // Preserve existing asset fields (hero/thumbnail) and update the link
        // label (assets.cta) and link text (assets.disclaimer) from the form.
        assets: {
          ...productRecord.assets,
          cta: parseRequiredStringField(linkLabel),
          disclaimer: parseRequiredStringField(linkText),
        },
        markers: normalizeMarkers(markers).map(markerRecord => ({
          ...markerRecord,
          weight: parseMarkerWeight(markerRecord.weight),
        })),
      });

      const parsedPriority = parseNumberField(priority);

      if (parsedPriority != null) {
        editedProductRecord.priority = parsedPriority;
      } else {
        delete editedProductRecord.priority;
      }

      onAccept(editedProductRecord);
    } catch (error) {
      console.log(error);
    }
  };

  const onCloseHandler = () => {
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick") {
          onCloseHandler();
        }
      }}
      disableEscapeKeyDown
      maxWidth={maxWidth}
      fullWidth={fullWidth}
    >
      <DialogTitle onClose={onCloseHandler}>{dialogTitle}</DialogTitle>

      <DialogTabs
        tabIndex={tabIndex}
        setTabIndex={setTabIndex}
        variant="fullWidth"
        scrollButtons
        allowScrollButtonsMobile
      >
        <Tab key={`tab0`} icon={<InfoIcon />} label="Info" />
        <Tab key={`tab1`} icon={<PeopleIcon />} label="Attributes" />
        <Tab key={`tab2`} icon={<MmsIcon />} label="Assets" />
      </DialogTabs>

      <DialogContent>
        {/* INFO TAB */}
        <DialogTab selectedIndex={tabIndex} index={0}>
          <Grid container>
            <Grid container item spacing={1} sx={{ marginTop: theme.spacing(1) }}>
              {/* Title */}
              <Grid item size={{ xs: 12 }}>
                <OmniTextField
                  sx={{ width: "100%" }}
                  label={"Title"}
                  required
                  type="textInput"
                  variant="outlined"
                  name={uuid()}
                  id={"productTitle"}
                  value={title}
                  error={titleHasError}
                  helperText={titleHasError ? "Title is required." : ""}
                  onChange={event => setTitle(event.target.value)}
                />
              </Grid>

              {/* Product + Priority + ID */}
              <Grid item size={{ xs: 12, sm: 6 }}>
                <OmniTextField
                  sx={{ width: "100%" }}
                  label={"Product"}
                  select
                  variant="outlined"
                  name={uuid()}
                  id={"productType"}
                  value={type}
                  onChange={event => setType(event.target.value)}
                >
                  {productOptions.map(productType => (
                    <OmniMenuItem key={productType.value} value={productType.value}>
                      {productType.label}
                    </OmniMenuItem>
                  ))}
                </OmniTextField>
              </Grid>

              <Grid item size={{ xs: 6, sm: 3 }}>
                <OmniTextField
                  sx={{ width: "100%" }}
                  label={"Priority"}
                  select
                  variant="outlined"
                  name={uuid()}
                  id={"productPriority"}
                  value={priority}
                  onChange={event => setPriority(event.target.value)}
                >
                  {priorityOptions.map(priorityOption => (
                    <OmniMenuItem key={priorityOption} value={priorityOption}>
                      {priorityOption}
                    </OmniMenuItem>
                  ))}
                </OmniTextField>
              </Grid>

              {/* Read-only record ID */}
              <Grid item size={{ xs: 6, sm: 3 }}>
                <OmniTextField
                  sx={{ width: "100%" }}
                  label={"ID"}
                  type="textInput"
                  variant="outlined"
                  name={uuid()}
                  id={"productId"}
                  value={productRecord.id == null ? "" : String(productRecord.id)}
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              {/* Start + End Date */}
              <Grid item size={{ xs: 12, sm: 6 }}>
                <OmniDatePicker
                  label={"Start Date"}
                  value={startDateValue}
                  name={uuid()}
                  id={"productStartDate"}
                  onChange={date => setStartDate(formatDateValue(date))}
                  format="MM/dd/yyyy"
                  fullWidth
                  maxDate={endDay || undefined}
                  slotProps={{
                    textField: { size: "small", margin: "dense", fullWidth: true },
                  }}
                />
              </Grid>

              <Grid item size={{ xs: 12, sm: 6 }}>
                <OmniDatePicker
                  label={"End Date"}
                  value={endDateValue}
                  name={uuid()}
                  id={"productEndDate"}
                  onChange={date => setEndDate(formatDateValue(date))}
                  format="MM/dd/yyyy"
                  fullWidth
                  minDate={startDay || undefined}
                  slotProps={{
                    field: { clearable: true },
                    // "Clear" sets the end date to empty, which represents an
                    // Ongoing campaign.
                    actionBar: { actions: ["clear", "today", "accept"] },
                    textField: {
                      size: "small",
                      margin: "dense",
                      fullWidth: true,
                      InputLabelProps: { shrink: true },
                      error: dateRangeHasError,
                      helperText: dateRangeHasError
                        ? "End date must be on or after the start date"
                        : "Leave blank for Ongoing",
                      // When no end date is set, the campaign runs indefinitely, so
                      // overlay "Ongoing" in place of the empty MM/DD/YYYY sections.
                      ...(endDate
                        ? {}
                        : {
                            sx: {
                              "& .MuiPickersSectionList-root": { opacity: 0 },
                              "& .MuiPickersInputBase-root::after": {
                                content: '"Ongoing"',
                                position: "absolute",
                                left: "14px",
                                top: "50%",
                                transform: "translateY(-50%)",
                                color: theme.palette.text.primary,
                                pointerEvents: "none",
                              },
                            },
                          }),
                    },
                  }}
                />
              </Grid>

              {/* Summary */}
              <Grid item size={{ xs: 12 }}>
                <OmniTextField
                  sx={{ width: "100%" }}
                  label={"Summary"}
                  type="textInput"
                  variant="outlined"
                  name={uuid()}
                  id={"productSummary"}
                  rowCount={4}
                  multiline={true}
                  value={summary}
                  onChange={event => setSummary(event.target.value)}
                />
              </Grid>

              {/* Archived flag */}
              <Grid item size={{ xs: 12 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={archived}
                      onChange={event => setArchived(event.target.checked)}
                      color="secondary"
                    />
                  }
                  label="Archived"
                />
              </Grid>
            </Grid>
          </Grid>
        </DialogTab>

        {/* AUDIENCE ATTRIBUTES TAB */}
        <DialogTab selectedIndex={tabIndex} index={1}>
          <Grid spacing={1} container size={{ xs: 12 }}>
            <Grid
              item
              size={{ xs: 12 }}
              sx={{ display: "flex", justifyContent: "flex-end", marginTop: theme.spacing(1) }}
            >
              <OmniResponsiveButton
                icon={<AddIcon />}
                buttonText="Attribute"
                tooltipTitle="Add audience attribute"
                onClick={onAddMarker}
              />
            </Grid>

            <Grid item size={{ xs: 12 }}>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={markerHeaderCellSx}>Actions</TableCell>
                      <TableCell sx={markerHeaderCellSx}>Weight</TableCell>
                      <TableCell sx={markerHeaderCellSx}>Attribute</TableCell>
                      <TableCell sx={markerHeaderCellSx} align="right">
                        Value
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {assignedMarkers.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          <Typography variant="body2" color="textSecondary">
                            No audience attributes assigned.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}

                    {assignedMarkers.map(markerRecord => (
                      <TableRow key={markerRecord.marker} hover>
                        <TableCell>
                          <Tooltip title="Actions">
                            <IconButton
                              size="small"
                              color="secondary"
                              sx={{ border: `1px solid ${theme.palette.divider}` }}
                              onClick={event => openMarkerMenu(event, markerRecord)}
                            >
                              <ListIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                        <TableCell>{markerRecord.weight}</TableCell>
                        <TableCell>{markerRecord.marker}</TableCell>
                        <TableCell align="right">
                          {markerRecord.value === true ? "True" : "False"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        </DialogTab>

        {/* ASSETS TAB */}
        <DialogTab selectedIndex={tabIndex} index={2}>
          <Grid container>
            <Typography
              variant="overline"
              sx={{ marginTop: theme.spacing(1), marginBottom: `-${theme.spacing(1)}` }}
            >
              Link Info
            </Typography>

            <Grid container item spacing={1}>
              {/* Link URL -> productRecord.url */}
              <Grid item size={{ xs: 12 }}>
                <OmniUrlField
                  sx={{ width: "100%" }}
                  label={"Link URL"}
                  variant="outlined"
                  name={uuid()}
                  id={"linkUrl"}
                  value={linkUrl}
                  allowRelative
                  onChange={event => setLinkUrl(event.target.value)}
                />
              </Grid>

              {/* Link Label -> productRecord.assets.cta */}
              <Grid item size={{ xs: 12, sm: 6 }}>
                <OmniTextField
                  sx={{ width: "100%" }}
                  label={"Link Label"}
                  type="textInput"
                  variant="outlined"
                  name={uuid()}
                  id={"linkLabel"}
                  value={linkLabel}
                  onChange={event => setLinkLabel(event.target.value)}
                />
              </Grid>

              {/* Link Text -> productRecord.assets.disclaimer */}
              <Grid item size={{ xs: 12, sm: 6 }}>
                <OmniTextField
                  sx={{ width: "100%" }}
                  label={"Link Text"}
                  type="textInput"
                  variant="outlined"
                  name={uuid()}
                  id={"linkText"}
                  value={linkText}
                  onChange={event => setLinkText(event.target.value)}
                />
              </Grid>
            </Grid>
          </Grid>
        </DialogTab>
      </DialogContent>

      <DialogActions>
        <Box
          sx={{
            paddingRight: theme.spacing(2),
            paddingBottom: 0,
            display: "flex",
            flexDirection: "row",
            userSelect: "none",
            webkitUserSelect: "none",
            position: "relative",
            flexGrow: 1,
          }}
        >
          {productRecord.id != null && props.onDelete != null && (
            <OmniResponsiveButton
              icon={<DeleteIcon />}
              onClick={() => setShowConfirm(true)}
              buttonText={deleteMessage}
              tooltipTitle=""
            />
          )}
        </Box>

        <Box
          sx={{
            flexGrow: 0,
            flexShrink: 1,
          }}
        >
          {cancelMessage && (
            <Button
              onClick={onCloseHandler}
              color="secondary"
              variant="contained"
              sx={{ marginRight: theme.spacing(2) }}
            >
              {cancelMessage}
            </Button>
          )}

          <Button
            onClick={onAcceptHandler}
            color="secondary"
            variant="contained"
            disabled={formHasError}
          >
            {okMessage}
          </Button>
        </Box>
      </DialogActions>

      {showConfirm && (
        <AlertDialog
          open={showConfirm}
          dialogTitle={`Confirm`}
          dialogMessage={"Are you sure you want to delete this Product?"}
          onAccept={() => {
            setShowConfirm(false);
            onDeleteHandler();
          }}
          onClose={() => {
            setShowConfirm(false);
          }}
        />
      )}

      <OmniMenu
        anchorEl={markerMenuAnchor}
        open={Boolean(markerMenuAnchor)}
        onClose={closeMarkerMenu}
      >
        <OmniMenuItem
          onClick={() => {
            onEditMarker(markerMenuRecord);
            closeMarkerMenu();
          }}
        >
          <EditIcon fontSize="large" color="secondary" sx={{ marginRight: theme.spacing(1) }} />
          <ListItemText disableTypography={true}>
            <Typography variant="h5" color="textSecondary">
              Edit
            </Typography>
          </ListItemText>
        </OmniMenuItem>
        <OmniMenuItem
          onClick={() => {
            onDeleteMarker(markerMenuRecord.marker);
            closeMarkerMenu();
          }}
        >
          <DeleteIcon fontSize="large" color="secondary" sx={{ marginRight: theme.spacing(1) }} />
          <ListItemText disableTypography={true}>
            <Typography variant="h5" color="textSecondary">
              Delete
            </Typography>
          </ListItemText>
        </OmniMenuItem>
      </OmniMenu>

      {selectedMarkerRecord != null && (
        <BotpressProductMarkersDialog
          open={selectedMarkerRecord != null}
          markerRecord={selectedMarkerRecord}
          attributeOptions={FINANCIAL_MARKERS}
          assignedMarkerKeys={assignedMarkerKeys}
          dialogTitle={
            selectedMarkerRecord.marker ? "Edit Audience Attribute" : "Add Audience Attribute"
          }
          onAccept={onAcceptMarker}
          onDelete={markerRecord => {
            onDeleteMarker(markerRecord.marker);
            setSelectedMarkerRecord(null);
          }}
          onClose={() => setSelectedMarkerRecord(null)}
        />
      )}
    </Dialog>
  );
};

export default BotpressProductsDialog;
//
